/**
 * SwiftUI's `ColorPicker`, page side: a `pickColor` host request opens the
 * browser's color chooser (`<input type="color">`, clicked inside the tap
 * that sent the request) at the current color and answers
 * `{ color: "#rrggbb" }` when the viewer picks one, or `{}` when they close
 * it (where the browser reports that with a `cancel` event; otherwise a
 * dismissal is never answered and the app's color stays as it was).
 */

import type { PickColorRequestParams } from './protocol';

export type PickColorResult = {
  color?: string;
};

/** Opens a color chooser at `color` (`#rrggbb`); resolves with the chosen color, or null. */
export type ColorChooser = (color: string) => Promise<string | null>;

export async function performPickColor(params: PickColorRequestParams, choose: ColorChooser | undefined): Promise<PickColorResult> {
  if (!choose) throw new Error('unsupported: this page cannot open a color chooser');
  const current = /^#[0-9a-f]{6}$/i.test(params.color ?? '') ? params.color!.toLowerCase() : '#000000';
  const chosen = await choose(current);
  return chosen ? { color: chosen.toLowerCase() } : {};
}

/** The browser's color chooser: a hidden `<input type="color">` clicked at once. */
export function chooseColor(color: string): Promise<string | null> {
  const input = document.createElement('input');
  input.type = 'color';
  input.value = color;
  input.style.cssText = 'position:fixed;left:-1000px;top:0;width:1px;height:1px;opacity:0';
  input.dataset['sbPicker'] = '';
  document.body.append(input);
  const result = new Promise<string | null>((resolve) => {
    const finish = (value: string | null): void => {
      input.remove();
      resolve(value);
    };
    input.addEventListener('change', () => finish(input.value), { once: true });
    input.addEventListener('cancel', () => finish(null), { once: true });
  });
  input.click();
  return result;
}
